import { useEffect, useMemo, useState } from "react";
import { Button, Form, Modal } from "react-bootstrap";
import { useDispatch } from "react-redux";
import { setNotification } from "features/Notification/notificationSlice";
import authHeader from "services/auth-header";
import API from "services/axios";

const BUILTIN_LABELS = ["INBOX", "SENT", "TRASH"];

export default function CreateLabelModal({
  show,
  onClose,
  labels,
  refreshLabels,
  isUpdateMode,
  formData,
  setFormData,
  submitFunction,
}) {
  const dispatch = useDispatch();

  /*   All States
   ********************************************* */

  const [formError, setFormError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [matchingLabels, setMatchingLabels] = useState([]);
  const [locations, setLocations] = useState([]);
  const [locationsLoading, setLocationsLoading] = useState(false);
  const [locationFetchError, setLocationFetchError] = useState("");

  /*   All Functions
   ********************************************* */

  const normalizedLabels = useMemo(() => {
    const apiLabels = labels.map((label) => label.name.trim().toLowerCase());
    const builtin = BUILTIN_LABELS.map((label) => label.toLowerCase());
    return Array.from(new Set([...apiLabels, ...builtin]));
  }, [labels]);

  const allLabelNames = useMemo(() => {
    const apiNames = labels.map((label) => label.name);
    return Array.from(new Set([...apiNames, ...BUILTIN_LABELS]));
  }, [labels]);

  const selectedLocation = useMemo(
    () =>
      locations.find(
        (location) => String(location.id) === String(formData?.locationId || "")
      ) || null,
    [locations, formData?.locationId]
  );

  const handleClose = () => {
    if (submitting) return;
    onClose();
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    const trimmedName = formData?.labelName?.trim() || "";

    if (!formData?.locationId) {
      setFormError("Please select a location.");
      return;
    }

    if (!selectedLocation) {
      setFormError("Selected location is invalid. Please choose again.");
      return;
    }

    if (!trimmedName) {
      setFormError("Label name is required.");
      return;
    }

    if (normalizedLabels.includes(trimmedName.toLowerCase())) {
      setFormError("A label with this name already exists.");
      return;
    }

    setFormError("");
    setSubmitting(true);

    try {
      await submitFunction(event);

      setFormData({ name: "", locationId: "" });
      handleClose();
    } catch (err) {
      const message =
        err?.response?.data?.message ||
        err?.message ||
        "Failed to create label.";
      setFormError(message);
      dispatch(setNotification({ type: "error", message }));
    } finally {
      setSubmitting(false);
    }
  };

  /*   All UseEffects
   ********************************************* */

  useEffect(() => {
    if (!show) {
      setFormError("");
      setSubmitting(false);
      setMatchingLabels([]);
      setLocations([]);
      setLocationFetchError("");
      if (setFormData) {
        setFormData({ name: "", locationId: "" });
      }
    }
  }, [show, setFormData]);

  useEffect(() => {
    if (!show) return;

    const trimmedValue = formData?.labelName?.trim() || "";
    const lowerTrimmedValue = trimmedValue.toLowerCase();
    const timer = setTimeout(() => {
      if (!trimmedValue) {
        setMatchingLabels([]);
        return;
      }

      const matches = allLabelNames.filter((name) =>
        name.toLowerCase().includes(lowerTrimmedValue)
      );

      setMatchingLabels(matches);
    }, 100);

    return () => clearTimeout(timer);
  }, [formData?.labelName, allLabelNames, show]);

  useEffect(() => {
    if (!show) return;

    let isActive = true;

    const fetchLocations = async () => {
      setLocationsLoading(true);
      setLocationFetchError("");
      try {
        const { data } = await API.get("/emails/locations/list", {
          headers: authHeader(),
        });

        if (!isActive) return;

        if (Array.isArray(data?.locations)) {
          if (isUpdateMode && formData?.locationId) {
            setLocations([...data.locations, { ...formData.location }]);
          } else {
            setLocations(data.locations);
          }
        } else {
          throw new Error("Failed to load locations.");
        }
      } catch (error) {
        if (!isActive) return;
        const message = error?.message || "Failed to load locations.";
        setLocationFetchError(message);
        dispatch(
          setNotification({
            type: "error",
            message,
          })
        );
      } finally {
        if (isActive) {
          setLocationsLoading(false);
        }
      }
    };

    fetchLocations();

    return () => {
      isActive = false;
    };
  }, [dispatch, show, isUpdateMode]);

  return (
    <Modal
      show={show}
      onHide={handleClose}
      contentClassName="custom-shadow-sm px-2 rounded-3"
    >
      <Form onSubmit={handleSubmit}>
        <Modal.Header closeButton>
          <Modal.Title>Add Team</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group controlId="labelLocation">
            <Form.Label>Location</Form.Label>
            <Form.Select
              value={formData?.locationId || ""}
              onChange={(event) => {
                const locationId = event.target.value;
                const selected = locations.find(
                  (loc) => String(loc.id) === String(locationId)
                );
                setFormData({
                  ...formData,
                  locationId,
                  name: selected?.locationName || formData?.labelName || "",
                });
                if (formError) setFormError("");
              }}
              disabled={submitting || locationsLoading}
            >
              <option value="">Select a location</option>
              {locations.map((location) => (
                <option key={location.id} value={location.id}>
                  {location.locationName}
                </option>
              ))}
            </Form.Select>
            {locationsLoading && (
              <div className="small text-muted mt-2">Loading locations...</div>
            )}
            {locationFetchError && (
              <div className="text-danger small mt-2">{locationFetchError}</div>
            )}
          </Form.Group>
          {formError && (
            <div className="alert alert-danger small mt-3">
              {formError === "Conflict" ? "Already Exists" : formError}
            </div>
          )}
          <Form.Group controlId="newLabelName" className="mt-3">
            <Form.Label>Label Name</Form.Label>
            <Form.Control
              value={formData?.labelName || ""}
              onChange={(event) => {
                setFormData({
                  ...formData,
                  labelName: event.target.value,
                });
                if (formError) setFormError("");
              }}
              placeholder="Enter label name"
              autoFocus
              disabled={submitting}
            />
          </Form.Group>

          {(formData?.labelName?.trim() || "") && matchingLabels.length > 0 && (
            <div className="alert alert-info py-2 mt-3 mb-0">
              <strong>Matching labels</strong>
              <div className="mt-1 d-flex flex-wrap gap-2">
                {matchingLabels.map((name) => (
                  <span key={name} className="badge bg-info text-dark">
                    {name}
                  </span>
                ))}
              </div>
            </div>
          )}
          {(formData?.labelName?.trim() || "") &&
            matchingLabels.length === 0 && (
              <div className="alert alert-success py-2 mt-3 mb-0">
                No existing team uses this exact text.
              </div>
            )}
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={handleClose}
            disabled={submitting}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            variant="primary"
            disabled={submitting || locationsLoading}
          >
            {isUpdateMode ? "Update" : "Add"}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
}
